import { useCallback, useMemo } from "react"
import { Accept, useDropzone } from "react-dropzone"
import cs from "classnames"

import styles from "./FileDropZone.module.scss"

export interface Props {
  disabled?: boolean
  droppedFiles?: File[]
  filePreview?: "inside" | "outside"
  accept?: Accept
  maxFiles?: number
  maxSize?: number
  dropzoneLabel?: React.ReactNode

  onFilesDropped: (files: File[]) => void
}

export const FileDropZone = ({
  accept,
  maxFiles,
  maxSize,
  disabled,
  droppedFiles,
  dropzoneLabel,
  filePreview = "inside",
  onFilesDropped,
}: Props) => {
  const handleFileDropped = useCallback((files: File[]) => onFilesDropped(files), [onFilesDropped])

  const { getRootProps, getInputProps, isDragActive } = useDropzone(
    useMemo(
      () => ({
        accept,
        onDropAccepted: handleFileDropped,
        disabled,
        maxFiles,
        maxSize,
        onDropRejected: (rejections) => {
          if (rejections[0]?.errors[0]?.code === "file-too-large") {
            alert(`File size cannot exceed ${maxSize ? `${maxSize / 1_000_000}MB` : "the limit"}`)
          }
        },
      }),
      [accept, disabled, handleFileDropped, maxFiles, maxSize]
    )
  )

  return (
    <div className={styles.root}>
      <div
        {...getRootProps()}
        className={cs(styles.inner, { [styles.highlight]: isDragActive || !!droppedFiles?.length })}
      >
        <input {...getInputProps()} />

        <div>
          {filePreview === "inside"
            ? !droppedFiles?.length
              ? dropzoneLabel ?? (
                  <>
                    Drop Files Here <br /> Or Click To Select Files
                  </>
                )
              : droppedFiles.map((file, i) => <span key={i}>{file.name}</span>)
            : dropzoneLabel ?? (
                <>
                  Drop Files Here <br /> Or Click To Select Files
                </>
              )}
        </div>
      </div>

      {filePreview === "outside" && !!droppedFiles?.length
        ? droppedFiles.map((file, i) => (
            <a href={URL.createObjectURL(file)} key={i}>
              {file.name}
            </a>
          ))
        : null}
    </div>
  )
}
